import apiClient, { prefetchApiCsrfToken } from "../common";
import type { BuyOrder } from "../types";

const JSON_API_MEDIA = "application/vnd.api+json";

export type CreateSupplyDocumentResult = {
  id: string;
  document_number: string;
};

function formatDateForApi(value: Date | string): string | undefined {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function supplierNameFromOrder(order: BuyOrder): string {
  const first = order.incoming_product_list?.[0];
  if (!first?.manufacturer) {
    return "";
  }
  return String(first.manufacturer);
}

export function buildLineItems(order: BuyOrder) {
  const lines = order.incoming_product_list ?? [];
  return lines
    .filter((line) => String(line.product_code ?? "").trim())
    .map((line) => {
      const bulks = Number(line.bulks) || 0;
      const unitsPerBulk = Number(line.units_per_bulk) || 0;
      const totalUnits =
        line.total_units != null
          ? Number(line.total_units)
          : bulks > 0 && unitsPerBulk > 0
            ? bulks * unitsPerBulk
            : undefined;
      const item: Record<string, string | number | boolean> = {
        "product-code": String(line.product_code).trim(),
      };
      if (line.description) {
        item.description = String(line.description);
      }
      if (bulks > 0) {
        item.bulks = bulks;
      }
      if (unitsPerBulk > 0) {
        item["units-per-bulk"] = unitsPerBulk;
      }
      if (totalUnits != null && !Number.isNaN(totalUnits) && totalUnits > 0) {
        item["total-units"] = totalUnits;
      }
      // Datos de compra: sólo viajan si el usuario los cargó. `0` y `false` son
      // valores legítimos (costo cero capturado a propósito, renglón exento),
      // así que se comparan contra null/undefined, no por truthiness.
      if (line.unit_cost != null) {
        const unitCost = Number(line.unit_cost);
        if (!Number.isNaN(unitCost)) {
          item["unit-cost"] = unitCost;
        }
      }
      const warehouseCode = String(line.warehouse_code ?? "").trim();
      if (warehouseCode) {
        item["warehouse-code"] = warehouseCode;
      }
      if (typeof line.applies_tax === "boolean") {
        item["applies-tax"] = line.applies_tax;
      }
      if (line.tax_pctg != null) {
        const taxPctg = Number(line.tax_pctg);
        if (!Number.isNaN(taxPctg)) {
          item["tax-pctg"] = taxPctg;
        }
      }
      if (line.discount_pctg != null) {
        const discountPctg = Number(line.discount_pctg);
        if (!Number.isNaN(discountPctg)) {
          item["discount-pctg"] = discountPctg;
        }
      }
      const uom = String(line.uom ?? "").trim();
      if (uom) {
        item.uom = uom;
      }
      return item;
    });
}

function buildCreatePayload(order: BuyOrder, simulationId: string) {
  const expectedReceipt = formatDateForApi(order.ata);
  const atawDate = order.ataw != null ? formatDateForApi(order.ataw) : undefined;
  const attributes: Record<string, string | unknown> = {
    "document-origin": "simulated",
    "document-type": "PO",
    "document-number": String(order.number ?? "").trim(),
    "document-status": "draft",
    simulation: String(simulationId),
  };
  if (expectedReceipt) {
    attributes["expected-receipt-date"] = expectedReceipt;
  }
  if (atawDate) {
    attributes["ataw-date"] = atawDate;
  }
  const supplierName = order.vendor_name || supplierNameFromOrder(order);
  if (supplierName) {
    attributes["supplier-name"] = supplierName;
  }
  // `supplier-code` es el `vendorCode` del ERP, no el nombre. Va en columna
  // propia y no en `supplier-id`, que se usa como texto libre.
  const supplierCode = String(order.vendor_code ?? "").trim();
  if (supplierCode) {
    attributes["supplier-code"] = supplierCode;
  }
  const lineItems = buildLineItems(order);
  if (lineItems.length > 0) {
    attributes["line-items"] = lineItems;
  }

  return {
    data: {
      type: "supply-documents",
      attributes,
    },
  };
}

export type CreateSupplyDocumentOptions = {
  /** Exportación a Monday: upsert por número y pone document-status en real. */
  forMondayExport?: boolean;
  /**
   * Envío a Profit: mismo upsert por número, pero el documento sigue en
   * `draft`. Materializa la orden antes de enviarla, porque las OC del
   * simulador viven en sesión y una sesión vencida a mitad del envío dejaría
   * una orden en el ERP sin nada acá que la explique.
   */
  forProfitExport?: boolean;
};

export async function apiCreateSupplyDocument(
  order: BuyOrder,
  simulationId: string,
  options?: CreateSupplyDocumentOptions,
): Promise<CreateSupplyDocumentResult> {
  await prefetchApiCsrfToken();
  const res = await apiClient.post(
    "supply-documents/",
    buildCreatePayload(order, simulationId),
    {
      params: options?.forMondayExport
        ? { for_monday_export: true }
        : options?.forProfitExport
          ? { for_profit_export: true }
          : {},
      headers: {
        Accept: JSON_API_MEDIA,
        "Content-Type": JSON_API_MEDIA,
      },
    },
  );
  const row = res.data?.data ?? res.data;
  const attrs = row?.attributes ?? {};
  return {
    id: String(row?.id ?? ""),
    document_number: String(
      attrs["document-number"] ?? attrs.document_number ?? order.number ?? "",
    ),
  };
}

export async function apiCreateSupplyDocumentsForOrders(
  orders: BuyOrder[],
  simulationId: string,
  options?: CreateSupplyDocumentOptions,
): Promise<CreateSupplyDocumentResult[]> {
  if (orders.length === 0) {
    return [];
  }
  const results: CreateSupplyDocumentResult[] = [];
  for (const order of orders) {
    results.push(await apiCreateSupplyDocument(order, simulationId, options));
  }
  return results;
}
